import { useEffect, useRef } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel: string;
  cancelLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// Bottom-sheet confirm dialog. Mirrors the DishSheet chrome (backdrop, slide-up,
// Escape, role="dialog") so the user already knows the dismissal pattern.
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    // Focus the title for screen readers; sighted users see the buttons.
    queueMicrotask(() => titleRef.current?.focus());
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={onCancel}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div
        style={{
          position: 'relative',
          background: 'var(--color-bg)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: '8px 24px max(20px, env(safe-area-inset-bottom))',
          boxShadow: 'var(--shadow-sheet)',
        }}
      >
        <div
          aria-hidden
          style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--color-divider)', margin: '8px auto 16px' }}
        />

        <h2
          id="confirm-dialog-title"
          ref={titleRef}
          tabIndex={-1}
          style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2, margin: 0, letterSpacing: '-0.01em', outline: 'none' }}
        >
          {title}
        </h2>

        {body && (
          <p style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--color-text-secondary)', margin: '8px 0 0' }}>
            {body}
          </p>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: 12,
              border: '1px solid var(--color-divider)',
              background: 'transparent',
              color: 'var(--color-primary)',
              fontSize: 16,
              fontWeight: 500,
              minHeight: 48,
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className="btn-primary"
            style={{
              flex: 1,
              minHeight: 48,
              background: destructive ? '#C4332B' : undefined,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
